import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react'
import './ProgramTimelineNav.css'
import ContentEngine from '../ContentEngine/ContentEngine'

/* helpers */
const EDGE_ZONE = 60

const BACKEND_URL = process.env.REACT_APP_API_BASE

const ArrowLeft = () => (
    <svg viewBox="0 0 24 24">
        <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
    </svg>
)

const ArrowRight = () => (
    <svg viewBox="0 0 24 24">
        <path d="M8.59 16.59 10 18l6-6-6-6-1.41 1.41L13.17 12z" />
    </svg>
)

/* Screen-type icons (tiny inline SVGs) */
const ScreenIcons = {
    playdynamex: (
        <svg viewBox="0 0 24 24" className="timeline-screen-icon">
            <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z" />
        </svg>
    ),
    assembly: (
        <svg viewBox="0 0 24 24" className="timeline-screen-icon">
            <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
        </svg>
    ),
    hex: (
        <svg
            viewBox="0 0 24 24"
            className="timeline-screen-icon"
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinejoin="miter"
        >
            <path d="M12 3l7 4v10l-7 4-7-4V7l7-4z" />
        </svg>
    ),
    simulate: (
        <svg viewBox="0 0 24 24" className="timeline-screen-icon">
            <path d="M8 5v14l11-7z" />
        </svg>
    ),
    play: (
        <svg
            viewBox="0 0 24 24"
            className="timeline-screen-icon"
            fill="currentColor"
        >
            <path d="M12 2l2 6 6 2-6 2-2 6-2-6-6-2 6-2 2-6z" />
        </svg>
    ),
    integrate: (
        <svg
            viewBox="0 0 24 24"
            className="timeline-screen-icon"
            fill="currentColor"
        >
            <path d="M7 4v4h4l-5 5 2 2 5-5v4h4V4H7z" />
        </svg>
    ),
}

/* StepNode */
const StepNode = React.memo(function StepNode({
    stepNumber,
    displayNumber,
    stepTitle,
    screenType,
    isActive,
    isCompleted,
    isOtherScreen,
    onHoverStart,
    onHoverEnd,
    onNavigate,
}) {
    let nodeClass = 'timeline-sub-node'
    if (isActive) nodeClass += ' timeline-sub-node--active'
    else if (isCompleted) nodeClass += ' timeline-sub-node--completed'
    if (isOtherScreen) nodeClass += ' timeline-sub-node--other-screen'

    const icon = ScreenIcons[screenType] || null

    return (
        <div
            className="timeline-sub-group"
            onMouseEnter={() => onHoverStart(stepNumber)}
            onMouseLeave={onHoverEnd}
        >
            {icon && (
                <span className="timeline-screen-icon-wrapper">{icon}</span>
            )}

            <span className="timeline-sub-tooltip">
                {stepTitle || `Step ${displayNumber}`}
            </span>

            <div
                className={nodeClass}
                onClick={() => onNavigate && onNavigate(displayNumber)}
            >
                <span className="timeline-sub-label">{displayNumber}</span>
            </div>
        </div>
    )
})

/* Small divider between screen groups */
const ScreenDivider = () => <div className="timeline-screen-divider" />

/**
 * ProgramTimelineNav
 */
function ProgramTimelineNav({
    currentStep = 1,
    totalSteps = 1,
    stepTitles = [],
    onStepClick,
}) {
    const trackRef = useRef(null)
    const rafRef = useRef(null)

    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    // Force re-render when ContentEngine notifies (program loaded, screen changed)
    const [engineTick, setEngineTick] = useState(0)
    useEffect(() => {
        return ContentEngine.subscribe(() => setEngineTick((n) => n + 1))
    }, [])

    const isPreviewMode =
        new URLSearchParams(window.location.search).get('preview') === 'true'
    const currentScreenIndex = ContentEngine.screenIndex

    // --- Build Screens from backend-provided step counts ---
    const { allScreens, currentScreenOffset } = useMemo(() => {
        let screens = ContentEngine.getAllScreenSteps()
        let offset = 0

        if (isPreviewMode) {
            const currentScreenData = screens.find(
                (s) => s.screenIndex === currentScreenIndex
            )
            screens = [
                {
                    screenIndex: currentScreenIndex,
                    stepCount: totalSteps,
                    offset: 0,
                    screenType:
                        currentScreenData?.screenType ||
                        ContentEngine.getCurrentScreen()?.type ||
                        '',
                },
            ]
        } else {
            let runningOffset = 0

            screens = screens.map((s) => {
                // For the active screen, trust the live props (in case
                // the backend count doesn't match the runtime count)
                const trueCount =
                    s.screenIndex === currentScreenIndex
                        ? totalSteps
                        : s.stepCount

                const updatedScreen = {
                    ...s,
                    stepCount: trueCount,
                    offset: runningOffset,
                }

                runningOffset += trueCount
                return updatedScreen
            })

            const activeScreenObj = screens.find(
                (s) => s.screenIndex === currentScreenIndex
            )
            offset = activeScreenObj ? activeScreenObj.offset : 0
        }

        return { allScreens: screens, currentScreenOffset: offset }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentScreenIndex, totalSteps, isPreviewMode, engineTick])

    // Storage key for completed tracking
    const storageKey = `timelineCompleted_${ContentEngine.subIndex}`

    const [completedSteps, setCompletedSteps] = useState([])

    useEffect(() => {
        const fetchCompletedSteps = async () => {
            const token = localStorage.getItem('userJWT')
            const currentSubprogram = ContentEngine.getCurrentSubprogram()

            if (!token || !currentSubprogram) return

            try {
                const res = await fetch(
                    `${BACKEND_URL}/api/progress/${currentSubprogram.id}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        credentials: 'include',
                    }
                )

                if (res.ok) {
                    const data = await res.json()
                    if (data.progress && data.progress.completedSteps) {
                        setCompletedSteps(data.progress.completedSteps)
                    }
                }
            } catch (e) {
                console.error('Failed to fetch timeline progress', e)
            }
        }

        fetchCompletedSteps()
    }, []) // Run once on mount

    // 3. SAVE TO DATABASE WHEN A NEW STEP IS COMPLETED
    useEffect(() => {
        if (!currentStep) return

        const timer = setTimeout(() => {
            const currentScreenData = allScreens.find(
                (s) => s.screenIndex === currentScreenIndex
            )
            if (currentScreenData && currentStep > currentScreenData.stepCount)
                return

            const globalStep = currentStep + currentScreenOffset

            setCompletedSteps((prev) => {
                // Only update if this step isn't already in the array
                if (!prev.includes(globalStep)) {
                    const updated = [...prev, globalStep]

                    // --- POST TO DATABASE ---
                    const token = localStorage.getItem('userJWT')
                    const currentSubprogram =
                        ContentEngine.getCurrentSubprogram()

                    if (token && currentSubprogram) {
                        fetch(`${BACKEND_URL}/api/progress/sync`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                subprogramId: currentSubprogram.id,
                                completedSteps: updated,
                            }),
                            credentials: 'include',
                        }).catch((err) =>
                            console.error('Failed to sync step', err)
                        )
                    }

                    return updated
                }
                return prev
            })
        }, 100)

        return () => clearTimeout(timer)
    }, [
        currentStep,
        currentScreenOffset,
        storageKey,
        allScreens,
        currentScreenIndex,
    ])

    /* Overflow detection */
    const updateOverflow = useCallback(() => {
        const el = trackRef.current
        if (!el) return
        setCanScrollLeft(el.scrollLeft > 2)
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2)
    }, [])

    useEffect(() => {
        const el = trackRef.current
        if (!el) return
        updateOverflow()
        el.addEventListener('scroll', updateOverflow, { passive: true })
        const ro = new ResizeObserver(updateOverflow)
        ro.observe(el)
        return () => {
            el.removeEventListener('scroll', updateOverflow)
            ro.disconnect()
        }
    }, [updateOverflow, totalSteps, allScreens.length])

    /* Auto-scroll to bring active step into view on mount/screen change */
    const activeGlobalStep = currentStep + currentScreenOffset
    useEffect(() => {
        const el = trackRef.current
        if (!el) return
        const activeNode = el.querySelector('.timeline-sub-node--active')
        if (activeNode) {
            const group = activeNode.closest('.timeline-sub-group')
            if (group) {
                const trackRect = el.getBoundingClientRect()
                const groupRect = group.getBoundingClientRect()
                const scrollTarget =
                    group.offsetLeft -
                    el.offsetLeft -
                    trackRect.width / 2 +
                    groupRect.width / 2
                el.scrollTo({
                    left: Math.max(0, scrollTarget),
                    behavior: 'smooth',
                })
            }
        }
    }, [activeGlobalStep, totalSteps, allScreens.length])

    /* Mouse-wheel horizontal scroll */
    useEffect(() => {
        const el = trackRef.current
        if (!el) return
        const onWheel = (e) => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault()
                el.scrollLeft += e.deltaY
            }
        }
        el.addEventListener('wheel', onWheel, { passive: false })
        return () => el.removeEventListener('wheel', onWheel)
    }, [totalSteps])

    /* Edge hover auto-scroll */
    const autoScrollSpeed = useRef(0)

    const startAutoScroll = useCallback(() => {
        const step = () => {
            const el = trackRef.current
            if (!el || autoScrollSpeed.current === 0) return
            el.scrollLeft += autoScrollSpeed.current
            rafRef.current = requestAnimationFrame(step)
        }
        cancelAnimationFrame(rafRef.current)
        rafRef.current = requestAnimationFrame(step)
    }, [])

    const stopAutoScroll = useCallback(() => {
        autoScrollSpeed.current = 0
        cancelAnimationFrame(rafRef.current)
    }, [])

    const handleTrackMouseMove = useCallback(
        (e) => {
            const el = trackRef.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            const x = e.clientX - rect.left
            const w = rect.width

            if (x < EDGE_ZONE) {
                const ratio = 1 - x / EDGE_ZONE
                autoScrollSpeed.current = -(1 + ratio * 5)
                startAutoScroll()
            } else if (x > w - EDGE_ZONE) {
                const ratio = 1 - (w - x) / EDGE_ZONE
                autoScrollSpeed.current = 1 + ratio * 5
                startAutoScroll()
            } else {
                stopAutoScroll()
            }
        },
        [startAutoScroll, stopAutoScroll]
    )

    const handleTrackMouseLeave = useCallback(() => {
        stopAutoScroll()
    }, [stopAutoScroll])

    const handleSubHoverStart = useCallback(() => {}, [])
    const handleSubHoverEnd = useCallback(() => {}, [])

    // Handle click on any step (global display number)
    const handleGlobalStepClick = useCallback(
        (globalDisplayNumber) => {
            for (let i = 0; i < allScreens.length; i++) {
                const screen = allScreens[i]
                const count = screen.stepCount
                const screenStart = screen.offset + 1
                const screenEnd = screen.offset + count
                if (
                    globalDisplayNumber >= screenStart &&
                    globalDisplayNumber <= screenEnd
                ) {
                    const localStep = globalDisplayNumber - screen.offset
                    if (screen.screenIndex === currentScreenIndex) {
                        if (onStepClick) onStepClick(localStep)
                    } else {
                        ContentEngine.goToScreen(screen.screenIndex, localStep)
                    }
                    return
                }
            }
        },
        [allScreens, currentScreenIndex, onStepClick]
    )

    useEffect(() => {
        return () => {
            cancelAnimationFrame(rafRef.current)
        }
    }, [])

    const totalGlobalSteps = allScreens.reduce((sum, s) => sum + s.stepCount, 0)
    if (totalGlobalSteps <= 0 && totalSteps <= 0) return null

    return (
        <div className="timeline-nav-wrapper">
            <div
                className={`timeline-arrow timeline-arrow--left${canScrollLeft ? ' timeline-arrow--visible' : ''}`}
            >
                <ArrowLeft />
            </div>

            <div
                className="timeline-track"
                ref={trackRef}
                onMouseMove={handleTrackMouseMove}
                onMouseLeave={handleTrackMouseLeave}
            >
                <div className="timeline-row">
                    {allScreens.map((screen, sIdx) => {
                        const count = screen.stepCount
                        if (count <= 0) return null

                        const isCurrentScreen =
                            screen.screenIndex === currentScreenIndex
                        const nodes = []

                        if (sIdx > 0) {
                            const prevHadSteps = allScreens
                                .slice(0, sIdx)
                                .some((prev) => prev.stepCount > 0)
                            if (prevHadSteps) {
                                nodes.push(
                                    <ScreenDivider key={`div-${sIdx}`} />
                                )
                            }
                        }

                        for (let localIdx = 0; localIdx < count; localIdx++) {
                            const localStep = localIdx + 1
                            const globalDisplayNumber =
                                screen.offset + localStep
                            const isActive =
                                isCurrentScreen && localStep === currentStep
                            const isCompleted =
                                completedSteps.includes(globalDisplayNumber)

                            const title = isCurrentScreen
                                ? stepTitles?.[localIdx]
                                : undefined

                            if (localIdx > 0) {
                                const prevGlobalStep = screen.offset + localIdx
                                const isPrevCompleted =
                                    completedSteps.includes(prevGlobalStep)

                                nodes.push(
                                    <div
                                        key={`conn-${screen.screenIndex}-${localIdx}`}
                                        className={`timeline-connector
                                            ${!isCurrentScreen ? ' timeline-connector--other' : ''}
                                            ${isPrevCompleted ? ' timeline-connector--completed' : ''}
                                        `}
                                    />
                                )
                            }

                            nodes.push(
                                <StepNode
                                    key={`step-${screen.screenIndex}-${localStep}`}
                                    stepNumber={localStep}
                                    displayNumber={globalDisplayNumber}
                                    stepTitle={title}
                                    screenType={screen.screenType}
                                    isActive={isActive}
                                    isCompleted={isCompleted}
                                    isOtherScreen={!isCurrentScreen}
                                    onHoverStart={handleSubHoverStart}
                                    onHoverEnd={handleSubHoverEnd}
                                    onNavigate={handleGlobalStepClick}
                                />
                            )
                        }

                        return (
                            <React.Fragment key={`screen-${sIdx}`}>
                                {nodes}
                            </React.Fragment>
                        )
                    })}
                </div>
            </div>

            <div
                className={`timeline-arrow timeline-arrow--right${canScrollRight ? ' timeline-arrow--visible' : ''}`}
            >
                <ArrowRight />
            </div>
        </div>
    )
}

export default React.memo(ProgramTimelineNav)
